import { useEffect, useMemo, useState } from 'react';
import { isMobile, isTablet } from 'react-device-detect';
import { screenLg, screenSm } from 'constants/styles/breakpoints';
import { format, subDays } from 'date-fns';
import RangeDatePicker from 'components/rangeDatePicker';
import ModalRangePicker from 'components/rangeDatePicker/ModalRangePicker';
import InputSelect from 'components/inputs/inputSelect';
import { ClearInput, DateFilter, DateRangeSelectContainer } from '../ReportingFiltersComponents';
import { dateFilterOptions } from '../ReportingFiltersConfig';
import { formatDateInUtc } from 'utils/helpers';
import Icon from 'components/icons';
import styled from 'styled-components';
import { Tooltip } from 'antd';

const CustomLabel = styled.span<{ showModalLayout?: boolean }>`
  font-weight: 700;
  color: ${({ showModalLayout, theme }) =>
    showModalLayout
      ? theme.default.colors.palette.Business_DataVizLilacDark
      : 'var(--black-dark, #1a2535)'};
`;

const CustomWrapper = styled.div<{ showModalLayout?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${({ showModalLayout }) => (showModalLayout ? '10px 0' : '0')};
`;

const Label = styled.span`
  color: var(--light-gray-darker, #57575c);

  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const CustomButton = styled.div<{ showModalLayout?: boolean }>`
  display: flex;
  padding: 0;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  height: ${({ showModalLayout }) => (showModalLayout ? '20px' : '43px')};
  :hover {
    border-radius: 14px;
    background: ${({ showModalLayout }) => !showModalLayout && 'var(--grey-lighter, #f2f2f7)'};
  }
  .ant-picker {
    padding: 0;
  }

  ${screenSm} {
    padding: 2px 4px;
  }

  ${screenLg} {
    padding: 8px 16px;
  }
`;

type SetDates = (from: string, to: string) => void;

const DateRangeFilter = ({
  setDates,
  showModalLayout = false,
}: {
  setDates: SetDates;
  showModalLayout?: boolean;
}) => {
  const dateFormat = 'yyyy-MM-dd';
  const dateLabelFormat = 'MMMM dd, yyyy';
  // Exclude the 'custom' option from the dropdown
  const selectableOptions = useMemo(
    () => dateFilterOptions.filter((opt) => opt.value !== 'custom'),
    []
  );
  const defaultOption = selectableOptions[0];
  const [dateFilterValue, setDateFilterValue] = useState(defaultOption);
  const [clearDateRange, setClearDateRange] = useState(false);
  const [dateRangeValue, setDateRangeValue] = useState(null);

  const cleanDateInputs = () => {
    setDateRangeValue(null);
    setClearDateRange(false);
  };
  const onClearDateInput = () => {
    setDateFilterValue(defaultOption);
    cleanDateInputs();
    setDates(
      format(subDays(new Date(), Number(defaultOption.value)), dateFormat),
      format(new Date(), dateFormat)
    );
  };

  const onDateSelectChange = (selectValue: string | number) => {
    cleanDateInputs();
    setDateFilterValue(selectableOptions.find((opt) => opt.value === selectValue) || defaultOption);
    setDates(
      format(subDays(new Date(), Number(selectValue)), dateFormat),
      format(new Date(), dateFormat)
    );
  };

  const onDateFilterChange = (value: [string, string], date?: any) => {
    setDates(value[0], value[1]);
    setDateFilterValue({
      value: 'custom',
      label: `${formatDateInUtc(new Date(value[0]), dateLabelFormat)} - ${formatDateInUtc(
        new Date(value[1]),
        dateLabelFormat
      )}`,
    });
    setClearDateRange(true);
    setDateRangeValue(date);
  };

  useEffect(() => {
    setDates(
      format(subDays(new Date(), Number(defaultOption.value)), dateFormat),
      format(new Date(), dateFormat)
    );
  }, []);

  return (
    <DateFilter isTablet={isTablet} showModalLayout={showModalLayout}>
      <CustomWrapper showModalLayout={showModalLayout}>
        {showModalLayout && <Label>Date range</Label>}
        <CustomButton showModalLayout={showModalLayout}>
          {showModalLayout || isMobile ? (
            <ModalRangePicker
              value={dateRangeValue}
              suffixIcon={
                <CustomLabel showModalLayout={showModalLayout}>Custom date range</CustomLabel>
              }
              onChange={(date, dateString) => onDateFilterChange(dateString, date)}
            />
          ) : (
            <RangeDatePicker
              value={dateRangeValue}
              suffixIcon={
                <CustomLabel showModalLayout={showModalLayout}>Custom date range</CustomLabel>
              }
              onChange={(date, dateString) => onDateFilterChange(dateString, date)}
            />
          )}
        </CustomButton>
      </CustomWrapper>
      <DateRangeSelectContainer showModalLayout={showModalLayout} isTablet={isTablet}>
        <InputSelect
          value={dateFilterValue.label}
          placeHolder={'Select a date'}
          options={selectableOptions}
          onChange={(selectValue) => onDateSelectChange(selectValue)}
        />
        {clearDateRange && (
          <ClearInput showModalLayout={showModalLayout} onClick={() => onClearDateInput()}>
            <div>
              <Tooltip title="Clear custom date range">
                <Icon icon="CircledCloseFilled" size="small" />
              </Tooltip>
            </div>
          </ClearInput>
        )}
      </DateRangeSelectContainer>
    </DateFilter>
  );
};

export default DateRangeFilter;
